import { HierarchyLevel, HierarchyLevelConst } from '../dtc-commerce-mmm-types';
import { normalizeChannelName as normalize } from './dtc-commerce-mmm-channel-helpers';

interface IconMapping {
  channels: Record<string, string[]>;
  platforms: Record<string, string[]>;
}

const isIconMapping = (val: unknown): val is IconMapping => {
  if (typeof val !== 'object' || val === null) return false;
  const obj = val as Record<string, unknown>;
  return typeof obj.channels === 'object' && typeof obj.platforms === 'object';
};

const buildReverseLookup = (section: Record<string, string[]>): Map<string, string> => {
  const map = new Map<string, string>();
  for (const [iconPath, aliases] of Object.entries(section)) {
    if (iconPath.startsWith('_')) continue;
    for (const alias of aliases) {
      map.set(alias.toLowerCase().trim(), `https://cdn.blisspointmedia.com/mmm/${iconPath}`);
    }
  }
  return map;
};

let channelLookup = new Map<string, string>();
let platformLookup = new Map<string, string>();
let initPromise: Promise<void> | null = null;

const initIconMapping = (): Promise<void> => {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      const res = await fetch('https://cdn.blisspointmedia.com/mmm/icon-mapping.json');
      if (!res.ok) return;
      const mapping: unknown = await res.json();
      if (!isIconMapping(mapping)) return;
      channelLookup = buildReverseLookup(mapping.channels);
      platformLookup = buildReverseLookup(mapping.platforms);
    } catch (err) {
      console.warn('Icon mapping not available:', err);
    }
  })();
  return initPromise;
};

let channelPlatforms = new Map<string, Set<string>>();

export const registerChannelPlatforms = (
  channels: { channel: string; platform: string }[]
): void => {
  const map = new Map<string, Set<string>>();
  for (const { channel, platform } of channels) {
    if (!channel || !platform) continue;
    if (platform.toLowerCase() === 'total') continue;
    const key = normalize(channel);
    const set = map.get(key) ?? new Set<string>();
    set.add(platform);
    map.set(key, set);
  }
  channelPlatforms = map;
};

const resolveChannelIconWithSolePlatformFallback = (channelName: string): string | null => {
  if (!channelName) return null;
  const direct = channelLookup.get(normalize(channelName));
  if (direct) return direct;
  const platforms = channelPlatforms.get(normalize(channelName));
  if (platforms && platforms.size === 1) {
    const [onlyPlatform] = platforms;
    return platformLookup.get(normalize(onlyPlatform)) ?? null;
  }
  return null;
};

const resolvePlatformIcon = (platformName: string): string | null => {
  if (!platformName) return null;
  return platformLookup.get(normalize(platformName)) ?? null;
};

export const resolveIcon = (
  channel: string,
  platform: string | undefined,
  level: HierarchyLevel
): string | null => {
  if (level === HierarchyLevelConst.CHANNEL) {
    return resolveChannelIconWithSolePlatformFallback(channel);
  }
  if (platform && platform.toLowerCase() !== 'total') {
    const platformIcon = resolvePlatformIcon(platform);
    if (platformIcon) return platformIcon;
  }
  return resolveChannelIconWithSolePlatformFallback(channel);
};

export { initIconMapping };
